import { generateClient } from 'aws-amplify/api'
import { generatePresignedUrlForPut } from '@/graphql/mutations'
import {
  getUploadFileFromValue,
  buildPresignedUploadHeaders,
} from '@/mixins/Organization/UploadKnowledgeBase.js'
import { getApiMessage } from '@/mixins/Extras/ApiResponse'
import { parseAwsJson } from '@/utils/graphql'
import sessionGraphqlMixin from '@/mixins/Organization/sessionGraphqlMixin'

export { getUploadFileFromValue } from '@/mixins/Organization/UploadKnowledgeBase.js'

const client = generateClient()

const ORG_LOGO_MAX_BYTES = 2 * 1024 * 1024
const ORG_LOGO_PREFIX = 'YANDLE-ORG-LOGS'

const IMAGE_MIME_BY_EXT = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  svg: 'image/svg+xml',
}

export function sanitizeOrgLogoFileName(name) {
  const base = String(name || 'logo')
    .replace(/^.*[\\/]/, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
  return base || 'logo'
}

export function buildOrgLogoS3Key(file) {
  const fileName = sanitizeOrgLogoFileName(file?.name)
  return `${ORG_LOGO_PREFIX}/${fileName}/${Date.now()}`
}

export function getOrgLogoExtension(file) {
  const normalized = getUploadFileFromValue(file)
  const name = normalized?.name?.toLowerCase() || ''
  if (name.endsWith('.png')) return 'png'
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'jpg'
  if (name.endsWith('.webp')) return 'webp'
  if (name.endsWith('.svg')) return 'svg'
  return null
}

export function getOrgLogoMimeType(file) {
  const ext = getOrgLogoExtension(file)
  if (!ext) return 'application/octet-stream'
  return IMAGE_MIME_BY_EXT[ext] || 'application/octet-stream'
}

export default {
  mixins: [sessionGraphqlMixin],
  methods: {
    async getPresignedUrlForPut(s3Key, fileType) {
      try {
        const result = await client.graphql({
          query: generatePresignedUrlForPut,
          variables: {
            input: {
              s3_key: s3Key,
              file_type: fileType,
            },
          },
        })
        return parseAwsJson(result.data?.generatePresignedUrlForPut)
      } catch (error) {
        return {
          status: 'ERROR',
          message: getApiMessage(error, 'Failed to get presigned URL'),
        }
      }
    },

    resolvePresignedUrl(response) {
      if (!response) return null

      const candidates = [
        response.presigned_url,
        response.presignedUrl,
        response.url,
        response.upload_url,
        response.uploadUrl,
        response.data?.presigned_url,
        response.data?.presignedUrl,
        response.data?.url,
        response.data?.upload_url,
        response.data?.uploadUrl,
      ]

      const match = candidates.find(
        (value) => typeof value === 'string' && value.trim(),
      )
      return match?.trim() || null
    },

    async fetchUploadToPresignedUrl(presignedUrl, file, contentType) {
      const headers = buildPresignedUploadHeaders(presignedUrl, contentType)

      let body = file
      if ((file instanceof Blob || file instanceof File) && !headers['Content-Type']) {
        body = await file.arrayBuffer()
      }

      try {
        const response = await fetch(presignedUrl, {
          method: 'PUT',
          body,
          headers,
        })

        if (response.ok) {
          return { ok: true }
        }

        const responseText = await response.text().catch(() => '')
        return {
          ok: false,
          message:
            responseText ||
            response.statusText ||
            `Upload failed (${response.status})`,
          status: response.status,
        }
      } catch (error) {
        const isNetworkCors =
          error?.name === 'TypeError' &&
          /failed to fetch|networkerror/i.test(error?.message || '')

        return {
          ok: false,
          message: isNetworkCors
            ? 'Upload blocked (CORS). Ensure the S3 bucket allows PUT from this app origin.'
            : getApiMessage(error, 'Upload failed'),
        }
      }
    },

    async uploadOrgLogo(fileInput) {
      const file = getUploadFileFromValue(fileInput)
      if (!file) {
        return { status: 'ERROR', message: 'Please select a logo file' }
      }

      if ((file.size ?? 0) > ORG_LOGO_MAX_BYTES) {
        return {
          status: 'ERROR',
          message: 'Logo must be 2 MB or smaller',
        }
      }

      const ext = getOrgLogoExtension(file)
      if (!ext) {
        return {
          status: 'ERROR',
          message: 'Only PNG, JPG, WEBP, or SVG images are allowed',
        }
      }

      const s3Key = buildOrgLogoS3Key(file)
      const contentType = getOrgLogoMimeType(file)

      const presigned = await this.getPresignedUrlForPut(s3Key, contentType)
      if (presigned?.status !== 'SUCCESS') {
        return {
          status: 'ERROR',
          message: getApiMessage(presigned, 'Failed to get logo upload URL'),
        }
      }

      const uploadUrl = this.resolvePresignedUrl(presigned)
      if (!uploadUrl) {
        return {
          status: 'ERROR',
          message: 'Upload URL missing from presigned response',
        }
      }

      const upload = await this.fetchUploadToPresignedUrl(
        uploadUrl,
        file,
        contentType,
      )
      if (!upload.ok) {
        return {
          status: 'ERROR',
          message: upload.message || 'Failed to upload logo',
        }
      }

      return {
        status: 'SUCCESS',
        s3_key: s3Key,
        message: presigned?.message || '',
      }
    },
  },
}
